import {
    getSiteContent,
    getServices,
    getServiceDetail,
    getFaqs,
    getBlogs,
    getAllBlogDetails,
} from "./api";
import { findBySlug } from "../utils/slug";
import serviceAreaFallback from "../data/home/serviceAreaData.json";
import serviceDetailContentFallback from "../data/home/serviceDetailContent.json";
import faqDataFallback from "../data/faqData.json";
import blogDataFallback from "../data/blogData.json";
import blogDetailsFallback from "../data/blogDetailsData.json";

/**
 * CMS content with static fallback: FIRST priority from API.
 * Local bundled JSON is used only if the API is unreachable or has no record.
 */

type Loader<T> = () => Promise<T>;

export interface ContentResult<T> {
    data: T;
    live: boolean;
}

const sectionCache = new Map<string, any>();

export const getCachedSection = <T>(section: string): T | undefined => sectionCache.get(section);

/**
 * Fetches a site-content section. FIRST priority from API.
 * Falls back to local bundled copy only if unreachable.
 */
export async function loadSection<T>(section: string, fallback: T): Promise<ContentResult<T>> {
    if (sectionCache.has(section)) {
        return { data: sectionCache.get(section), live: true };
    }

    try {
        const response = await getSiteContent(section);
        const main = response?.main ?? (response && typeof response === "object" && !Array.isArray(response) ? Object.values(response)[0] : response);

        const usable = main != null && (typeof main === "object" ? Object.keys(main).length > 0 : true);

        if (usable) {
            sectionCache.set(section, main);
            return { data: main as T, live: true };
        }
    } catch (err) {
        console.warn(`CMS section '${section}' unreachable, using local fallback:`, err);
    }

    return { data: fallback, live: false };
}

/** List endpoint loader with static fallback. */
export async function loadList<T>(loader: Loader<T[]>, fallback: T[]): Promise<ContentResult<T[]>> {
    try {
        const rows = await loader();
        if (Array.isArray(rows) && rows.length > 0) {
            return { data: rows, live: true };
        }
    } catch {
        /* falls through */
    }
    return { data: fallback, live: false };
}

// ── Services CMS ─────────────────────────────────────────────────────────────

let cachedServices: any = null;
export const getCachedServices = (): any => cachedServices;

export async function loadServices(): Promise<any> {
    if (cachedServices) return cachedServices;

    try {
        const res = await getServices();
        if (res && Array.isArray(res.services) && res.services.length > 0) {
            cachedServices = res;
            return res;
        }
    } catch (err) {
        console.warn("Services API unreachable, using local fallback:", err);
    }

    return serviceAreaFallback;
}

export async function loadServiceDetail(slug: string): Promise<any | null> {
    try {
        const servicesData = await loadServices();
        const match = findBySlug<any>(servicesData?.services ?? [], slug);
        if (match && match.id) {
            const detail = await getServiceDetail(match.id);
            if (detail) return detail;
        }
    } catch (err) {
        console.warn("Service detail API unreachable, using local fallback:", err);
    }

    const fallbackMatch = findBySlug<any>(serviceDetailContentFallback as any[], slug);
    return fallbackMatch || null;
}

// ── FAQs CMS ─────────────────────────────────────────────────────────────────

let cachedFaqs: any[] | null = null;
export const getCachedFaqs = (): any[] | null => cachedFaqs;

export async function loadFaqs(): Promise<any[]> {
    if (cachedFaqs) return cachedFaqs;

    try {
        const rows = await getFaqs();
        if (Array.isArray(rows) && rows.length > 0) {
            const valid = rows.filter((f: any) => !f.isDeleted);
            if (valid.length > 0) {
                cachedFaqs = valid;
                return valid;
            }
        }
    } catch (err) {
        console.warn("FAQs API unreachable, using local fallback:", err);
    }

    return faqDataFallback.faqs || [];
}

// ── Blogs CMS ────────────────────────────────────────────────────────────────

let cachedBlogs: any = null;
let cachedBlogDetails: any[] | null = null;

export const getCachedBlogs = (): any => cachedBlogs;
export const getCachedBlogDetails = (): any[] | null => cachedBlogDetails;

export async function loadBlogs(): Promise<any> {
    if (cachedBlogs) return cachedBlogs;

    try {
        const rows = await getBlogs();
        if (Array.isArray(rows) && rows.length > 0) {
            cachedBlogs = rows[0];
            return rows[0];
        }
    } catch (err) {
        console.warn("Blogs API unreachable, using local fallback:", err);
    }

    return (blogDataFallback as any) || null;
}

export async function loadBlogDetails(): Promise<any[]> {
    if (cachedBlogDetails) return cachedBlogDetails;

    try {
        const rows = await getAllBlogDetails();
        if (Array.isArray(rows) && rows.length > 0) {
            const valid = rows.filter((b: any) => !b.isDeleted);
            if (valid.length > 0) {
                cachedBlogDetails = valid;
                return valid;
            }
        }
    } catch (err) {
        console.warn("Blog details API unreachable, using local fallback:", err);
    }

    return blogDetailsFallback as any[];
}

export async function loadBlogDetailBySlug(slug: string): Promise<any | null> {
    try {
        const all = await loadBlogDetails();
        const match = findBySlug<any>(all, slug);
        if (match) return match;
    } catch {
        /* falls through to local fallback */
    }

    return findBySlug<any>(blogDetailsFallback as any[], slug) || null;
}
